import React from 'react';
import { connect } from 'react-redux';

import { AccountType } from 'api/Location/model/AccountType';
import { RetailerGettingStartedType } from 'api/Location/model/RetailerGettingStartedType';
import { UserAccountId } from 'api/UserAccount/model/UserAccountId';
import { ISignUpFormState } from 'shared/components/SignUpOrSignIn/SignUpForm/reducers';

import { AddressFormFieldName } from 'shared/components/AddressForm/AddressForm';
import { ConnectedEmbeddedSignUpForm, IConnectedEmbeddedSignUpFormProps } from 'shared/components/SignUpOrSignIn/EmbeddedSignUpForm/EmbeddedSignUpForm';
import { IEmbeddedSignUpFormState } from 'shared/components/SignUpOrSignIn/EmbeddedSignUpForm/reducers';
import { SignUpOptionsFormActions } from 'shared/components/SignUpOrSignIn/SignUpOptions/actions';
import { ConnectedSlimCreateRetailerForm } from 'shared/components/SignUpOrSignIn/SlimCreateRetailerForm/SlimCreateRetailerForm';
import { IValidationInputData } from 'shared/components/ValidationInput';
import { ISignUpAndLoginButtonsState } from '../SignUpAndLoginButtons/reducers';
import { RetailerOnrampFormActions, RetailerOnrampFormActionInterfaces } from './actions';

import { DjangoApiManager } from 'shared/api/DjangoApiManager';
import { RuntimeException } from 'shared/lib/general/exceptions/RuntimeException';
import { PathUtils } from 'shared/utils/pathUtils';
import { BevSpotDispatch } from 'shared/components/Provider';

import 'shared/css/MakeItSpin.scss';

const validRetailerGettingStartedTypes = new Set<RetailerGettingStartedType>([
    'inventory_template', 'inventory_template_food', 'recipe_costing', 'recipe_costing_food', 'bar_manager',
    'kitchen_manager', 'owner_beverage', 'owner_food', 'drink_price_tool', 'invoice_upload', 'invoice_upload_food',
    'lightspeed', 'square', 'toast', 'upserve', 'ambit', 'troon'
]);

export const RETAILER_REQUIRED_DOWNLOAD_GATE_MOUNT_ELEMENT_ID = 'retailer-required-download-gate-mount'; // Note: This should match the ID of the mount element for this component because we've decided to set the needed data attribute there
const RETAILER_ONRAMP_TYPE_ATTRIBUTE_NAME = 'data-retailer-onramp-type';

const djangoApiManager = new DjangoApiManager();

interface IRetailerOnrampFormProps {
    signUpAndLoginButtonsState : ISignUpAndLoginButtonsState;
    embeddedSignUpFormState : IEmbeddedSignUpFormState;
}

interface IConnectedRetailerOnrampFormProps extends IRetailerOnrampFormProps {
    dispatch : BevSpotDispatch<IRetailerOnrampFormProps, { services: RetailerOnrampFormActionInterfaces.IServices }>;
}

class RetailerOnrampForm extends React.Component<IConnectedRetailerOnrampFormProps, object> {

    private retailerOnrampType : RetailerGettingStartedType | null = null; // can add additional values as they come up
    private retailerAccountType : AccountType = AccountType.BEVERAGE;

    public UNSAFE_componentWillMount() {
        const RetailerOnrampFormMountElement : HTMLElement | null = document.getElementById(RETAILER_REQUIRED_DOWNLOAD_GATE_MOUNT_ELEMENT_ID);

        if (RetailerOnrampFormMountElement) {
            const retailerOnrampTypeAttributeValue = RetailerOnrampFormMountElement.getAttribute(RETAILER_ONRAMP_TYPE_ATTRIBUTE_NAME);
            RetailerOnrampFormMountElement.removeAttribute(RETAILER_ONRAMP_TYPE_ATTRIBUTE_NAME);

            if (!retailerOnrampTypeAttributeValue) {
                djangoApiManager.logJsError(
                    'Could not get retailer onramp type data attribute. Found element with Id: ' + RETAILER_REQUIRED_DOWNLOAD_GATE_MOUNT_ELEMENT_ID + 'Was expecting attribute: ' + RETAILER_ONRAMP_TYPE_ATTRIBUTE_NAME,
                    null,
                    new RuntimeException('Could not get retailer onramp type data attribute'),
                    true
                );
            }

            if (retailerOnrampTypeAttributeValue !== null && !validRetailerGettingStartedTypes.has(retailerOnrampTypeAttributeValue as RetailerGettingStartedType)) {
                djangoApiManager.logJsError(
                    'Invalid retailer onramp type: ' + retailerOnrampTypeAttributeValue,
                    null,
                    new RuntimeException('Invalid retailer onramp type'),
                    true
                );
                this.retailerOnrampType = null;
            } else {
                this.retailerOnrampType = retailerOnrampTypeAttributeValue as RetailerGettingStartedType;
            }
        } else {
            djangoApiManager.logJsError(
                'Could not find mount point for download gate when trying to get download url. Was expecting ' + RETAILER_REQUIRED_DOWNLOAD_GATE_MOUNT_ELEMENT_ID,
                null,
                new RuntimeException('Could not find mount point for download gate'),
                true
            );
            this.retailerOnrampType = null;
        }

        switch (this.retailerOnrampType) {
            case 'inventory_template_food':
            case 'recipe_costing_food':
            case 'owner_food':
            case 'kitchen_manager':
            case 'invoice_upload_food':
                this.retailerAccountType = AccountType.FOOD;
                break;
            default:
                this.retailerAccountType = AccountType.BEVERAGE;
                break;
        }
    }

    public render() {
        const {
            dispatch,
            signUpAndLoginButtonsState,
            embeddedSignUpFormState,
        } = this.props;

        if (typeof(signUpAndLoginButtonsState.loggedInUser) === 'undefined') {
            return (
                <div>
                    <span className="bevicon bevico-spinner2 make-it-spin loading-spinner"/>
                </div>
            );
        }

        if ((signUpAndLoginButtonsState.loggedInUser === null)) {
            let aboveForm : JSX.Element | null = null;
            let belowForm : JSX.Element | null = null;

            if (embeddedSignUpFormState.signUpForm.hasSuccessfullySubmitted) {
                aboveForm = (
                    <div>
                        <h5>You&rsquo;ve unlocked this resource!</h5>
                        { /* downloadElement */ }
                    </div>
                );
            } else {
                const onWantsToLogIn = () => {
                    dispatch(SignUpOptionsFormActions.onSetActiveForm('signInForm'));
                    dispatch(SignUpOptionsFormActions.onSetFormIsShown(true));
                };

                aboveForm  = (
                    <div>
                        <h5>To access this resource, sign up for BevSpot free!</h5>
                    </div>
                );
                belowForm = (
                    <p className="small">
                        &nbsp; <br/>Already have a BevSpot account?<br/>
                        <a onClick={ onWantsToLogIn }>Log in</a> to get access.
                    </p>
                );
            }

            const connectedEmbeddedSignUpFormProps : IConnectedEmbeddedSignUpFormProps = {
                additionalFieldsAreShown: true,
                additionalRetailerFieldsAreRequired: true,
                onSuccessfulSignUp: (userAccountIdentifier : UserAccountId, signUpFormState : ISignUpFormState) => {
                    return dispatch(RetailerOnrampFormActions.onCreateRetailer(
                                        userAccountIdentifier,
                                        true,
                                        signUpFormState.validationInputDataByFieldName.barName.value,
                                        signUpFormState.validationInputDataByFieldName,
                                        signUpFormState.validationInputDataByFieldName.firstName.value,
                                        signUpFormState.validationInputDataByFieldName.lastName.value,
                                        signUpFormState.validationInputDataByFieldName.phone.value,
                                        signUpFormState.validationInputDataByFieldName.emailAddress.value,
                                        this.retailerAccountType,
                                        this.retailerOnrampType));
                },
            };

            return (
                <div className="download-gate">
                    { aboveForm }
                    <ConnectedEmbeddedSignUpForm { ...connectedEmbeddedSignUpFormProps }/>
                    { belowForm }
                </div>
            );
        }

        return (
            <div className="retailer-required-download-gate">
                <p>
                    Welcome back{ (signUpAndLoginButtonsState.loggedInUser.userInfo === null) ? '' : ', ' + signUpAndLoginButtonsState.loggedInUser.userInfo.firstName }!
                </p>

                { signUpAndLoginButtonsState.loggedInUser.userInfo !== null && signUpAndLoginButtonsState.loggedInUser.userInfo.hasAccessibleRetailers &&
                    <p>
                        { (this.retailerOnrampType === 'inventory_template' || this.retailerOnrampType === 'inventory_template_food') &&
                            <p>&nbsp; <br/>You can access your inventory spreadsheet from <a href={ PathUtils.getAbsolutePathForRequest('/account/item_manager') }>My Items</a> in your account.</p>
                        }
                        { (this.retailerOnrampType === 'recipe_costing' || this.retailerOnrampType === 'recipe_costing_food') &&
                            <p>&nbsp; <br/>You can access recipe costing from <a href={ PathUtils.getAbsolutePathForRequest('/inventory/history/?recordPrep=true') }>Prepared Items</a> in your account.</p>
                        }
                        { (this.retailerOnrampType === 'invoice_upload' || this.retailerOnrampType === 'invoice_upload_food') &&
                            <p>&nbsp; <br/>Upload your invoices to get started from <a href={ PathUtils.getAbsolutePathForRequest('/account/item_manager') }>My Items</a> in your account.</p>
                        }
                    </p>
                }

                { signUpAndLoginButtonsState.loggedInUser.userInfo !== null && !signUpAndLoginButtonsState.loggedInUser.userInfo.hasAccessibleRetailers &&
                    <div>
                        <div>
                            <h5>To access this resource, sign up for BevSpot free!</h5>
                        </div>
                        <ConnectedSlimCreateRetailerForm
                            onSubmit={ this.onSubmitRetailerForm }
                        />
                    </div>
                }

                { signUpAndLoginButtonsState.loggedInUser.userInfo === null || signUpAndLoginButtonsState.loggedInUser.userInfo.hasAccessibleRetailers &&
                    <p>
                        &nbsp; <br/>Remember, by joining you&rsquo;ve unlocked more resources! Check them out on your <a href={ PathUtils.getAbsolutePathForRequest('/user') }>user dashboard</a>
                    </p>
                }
            </div>
        );
    }

    private readonly onSubmitRetailerForm = (retailerName : string, addressForm : {[fieldName in AddressFormFieldName] : IValidationInputData }) => {
        const {
            signUpAndLoginButtonsState,
            dispatch,
        } = this.props;

        if (signUpAndLoginButtonsState.loggedInUser === null || typeof signUpAndLoginButtonsState.loggedInUser === 'undefined' || signUpAndLoginButtonsState.loggedInUser.userInfo === null) {
            throw new RuntimeException('unexpected no logged in user');
        }

        const userAccountIdentifier = signUpAndLoginButtonsState.loggedInUser.userId;
        return dispatch(RetailerOnrampFormActions.onCreateRetailer(
                            userAccountIdentifier,
                            false,
                            retailerName,
                            addressForm,
                            signUpAndLoginButtonsState.loggedInUser.userInfo.firstName,
                            signUpAndLoginButtonsState.loggedInUser.userInfo.lastName,
                            signUpAndLoginButtonsState.loggedInUser.userInfo.phoneNumber || '', // TODO? do we need this?
                            signUpAndLoginButtonsState.loggedInUser.userInfo.emailAddress,
                            this.retailerAccountType,
                            this.retailerOnrampType));
    }
}

const mapStateToProps = (state : IRetailerOnrampFormProps) : IRetailerOnrampFormProps => {
    return {
        signUpAndLoginButtonsState : state.signUpAndLoginButtonsState,
        embeddedSignUpFormState : state.embeddedSignUpFormState,
    };
};

export const ConnectedRetailerOnrampForm = connect<IRetailerOnrampFormProps, object, object, IRetailerOnrampFormProps>(mapStateToProps)(RetailerOnrampForm);
